import { Spin } from 'antd'
import { transformDocumentsToTableData } from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { useDocumentThumbnail } from 'features/knowledgeDocumentViewer/hooks/useDocumentThumbnail'
import { useMemo } from 'react'
import styled from 'styled-components'
import { SystemDocumentEntry } from 'types'

import { Box } from './types'

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 10rem;
  max-width: 16rem;
`

const TooltipLabel = styled.div`
  font-size: 0.625rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TooltipType = styled.div`
  font-size: 0.725rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const ThumbnailContainer = styled.div`
  width: 100%;
  height: 8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${(props) => props.theme.forecast.cardGroupBackground};
  border-radius: 0.25rem;
`

const ThumbnailImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

const DocumentTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(props) => props.theme.forecast.defaultText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-bottom: 0.5rem;
`

const ClickHint = styled.div`
  font-size: 0.625rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  padding-top: 0.375rem;
`

interface OffPageConnectorTooltipProps {
  box: Box
  systemDocumentEntries: SystemDocumentEntry[]
  linkedDocumentCount: number
}

export function OffPageConnectorTooltip({
  box,
  systemDocumentEntries,
  linkedDocumentCount,
}: OffPageConnectorTooltipProps) {
  const linkedDocument = useMemo(() => {
    const boxText = (box.label ?? '').toLowerCase()
    const matches = systemDocumentEntries.filter((document) => {
      const title = document.entry.data.title?.toLowerCase() ?? ''
      const drawingNumber =
        document.entry.data.drawing_number?.toLowerCase() ?? ''
      return title.includes(boxText) || drawingNumber.includes(boxText)
    })
    return matches.length > 0 ? matches[0] : null
  }, [box.label, systemDocumentEntries])

  const documentTableRow = useMemo(() => {
    if (!linkedDocument) return null
    const rows = transformDocumentsToTableData([linkedDocument])
    return rows[0] ?? null
  }, [linkedDocument])

  const { thumbnailUrl, isLoading } = useDocumentThumbnail({
    document: documentTableRow,
    scale: 1,
  })

  const documentTitle =
    linkedDocument?.entry.data.document_title ||
    linkedDocument?.entry.data.title ||
    linkedDocument?.entry.data.filename

  return (
    <TooltipContainer>
      <TooltipLabel>{box.label || `Box ${box.id}`}</TooltipLabel>

      {linkedDocument && (
        <>
          {documentTitle && <DocumentTitle>{documentTitle}</DocumentTitle>}
          <ThumbnailContainer>
            {isLoading ? (
              <Spin size='small' />
            ) : thumbnailUrl ? (
              <ThumbnailImage src={thumbnailUrl} alt={documentTitle ?? ''} />
            ) : null}
          </ThumbnailContainer>
        </>
      )}

      {linkedDocumentCount > 1 && (
        <TooltipType>{linkedDocumentCount} linked documents</TooltipType>
      )}

      <ClickHint>Click to open • Cmd/Ctrl + click for new tab</ClickHint>
    </TooltipContainer>
  )
}
